import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  Link,
} from '@chakra-ui/react';
import {
  TableRoot,
  TableHeader,
  TableBody,
  TableRow,
  TableColumnHeader,
  TableCell,
} from '@/components/ui/table';
import {
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
} from '@/components/ui/menu';
import { Tooltip } from '@/components/ui/tooltip';
import { toaster } from '@/components/ui/toaster';
import { useState, useEffect } from 'react';
import { FiPower, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { RiWhatsappLine, RiMailLine, RiPhoneLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

import {
  getChannelsList,
  updateChannel,
} from '@/features/channels/channels.service';
import { Channel } from '@/features/channels/channels.types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { defaultApiClient } from '@/services/api-client';

export default function ChannelList() {
  const [transitioningChannels, setTransitioningChannels] = useState<string[]>(
    [],
  );
  const [channelChatbots, setChannelChatbots] = useState<Record<string, { id: string; name: string } | null>>({});

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: channels, isLoading: loading } = useQuery({
    queryKey: ['channels', page, perPage],
    queryFn: () =>
      getChannelsList({
        page,
        perPage,
        sort: 'created_at',
        order: 'DESC',
        filter: {},
      }),
    select: (data) => data.data,
  });

  // Fetch linked chatbots for all channels
  useEffect(() => {
    const fetchChatbots = async () => {
      if (!channels || channels.length === 0) return;

      const chatbotPromises = channels.map(async (channel) => {
        try {
          // Get linked chatbot ID
          const { data: linkData } = await defaultApiClient.get<{ data: string | null }>(
            `/api/v1/channels/${channel.id}/chatbot`
          );

          if (linkData?.data) {
            // Fetch chatbot details
            const { data: chatbotData } = await defaultApiClient.get<{ data: { id: string; name: string } }>(
              `/api/v1/chatbots/${linkData.data}`
            );
            return { channelId: channel.id, chatbot: chatbotData?.data || null };
          }
          return { channelId: channel.id, chatbot: null };
        } catch (error) {
          console.error(`Error fetching chatbot for channel ${channel.id}:`, error);
          return { channelId: channel.id, chatbot: null };
        }
      });

      const results = await Promise.all(chatbotPromises);
      const chatbotMap: Record<string, { id: string; name: string } | null> = {};
      results.forEach(({ channelId, chatbot }) => {
        chatbotMap[channelId] = chatbot;
      });
      setChannelChatbots(chatbotMap);
    };

    fetchChatbots();
  }, [channels]);

  const getChannelIcon = (type: string) => {
    if (!type) return RiMailLine;
    switch (type.toLowerCase()) {
      case 'whatsapp':
        return RiWhatsappLine;
      case 'email':
        return RiMailLine;
      case 'sms':
        return RiPhoneLine;
      default:
        return RiMailLine;
    }
  };

  const getChannelIconColor = (type: string) => {
    if (!type) return 'gray.500';
    switch (type.toLowerCase()) {
      case 'whatsapp':
        return 'green.500';
      case 'email':
        return 'blue.500';
      case 'sms':
        return 'purple.500';
      default:
        return 'gray.500';
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      WORKING: 'green',
      CONNECTED: 'green',
      PENDING: 'yellow',
      FAILED: 'red',
      STARTING: 'blue',
      STOPPED: 'gray',
      SCAN_QR_CODE: 'purple',
      STOPPING: 'orange',
      INITIALIZING: 'blue',
    };
    return statusColors[status?.toUpperCase()] || 'gray';
  };

  const getTypeColor = (type: string) => {
    if (!type) return 'gray';
    switch (type.toLowerCase()) {
      case 'whatsapp':
        return 'green';
      case 'email':
        return 'blue';
      case 'sms':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const handleToggleActive = async (channel: Channel) => {
    setTransitioningChannels((prev) => [...prev, channel.id]);

    try {
      await updateChannel({
        id: channel.id,
        data: { is_active: !channel.is_active },
        previousData: channel as Channel,
      });

      // Refresh the channels list
      await queryClient.invalidateQueries({
        queryKey: ['channels', page, perPage],
      });

      toaster.create({
        title: 'Success',
        description: `Channel ${channel.is_active ? 'deactivated' : 'activated'} successfully`,
        type: 'success',
      });
    } catch (error) {
      console.error('Error updating channel:', error);
      toaster.create({
        title: 'Error',
        description: 'Failed to update channel status',
        type: 'error',
      });
    } finally {
      setTransitioningChannels((prev) =>
        prev.filter((id) => id !== channel.id),
      );
    }
  };

  const renderStatus = (channel: Channel) => {
    const status = channel.status?.toUpperCase() || 'UNKNOWN';
    const isTransitioning = transitioningChannels.includes(channel.id);

    if (isTransitioning) {
      return (
        <Flex align="center">
          <Spinner size="sm" mr={2} />
          <Badge colorScheme={getStatusColor(status)}>UPDATING</Badge>
        </Flex>
      );
    }

    return <Badge colorScheme={getStatusColor(status)}>{status}</Badge>;
  };

  const renderType = (type: string) => {
    const displayType = type || 'unknown';
    return (
      <Badge colorScheme={getTypeColor(type)} textTransform="uppercase">
        {displayType}
      </Badge>
    );
  };

  const renderRequiresAuth = (requiresAuth: boolean) => {
    return (
      <Badge colorScheme={requiresAuth ? 'red' : 'green'}>
        {requiresAuth ? 'YES' : 'NO'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading channels...</Text>
      </Box>
    );
  }

  const total = channels?.length || 0;

  return (
    <Box display="flex" flexDirection="column" height="100%">
      {/* Scrollable table container with conversations-style design */}
      <Box
        position="relative"
        flex="1"
        overflowX="auto"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="xl"
        bg="white"
        boxShadow="md"
        css={{
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0aec0',
          },
          // Fix sticky positioning during horizontal scroll
          '& table': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
        }}
      >
        <TableRoot>
          <TableHeader>
            <TableRow>
              {/* Fixed NAME Column - Sticky for both horizontal and vertical scrolling */}
              <TableColumnHeader
                position="sticky"
                left="0"
                top={0}
                zIndex={15}
                bg="white"
                borderRightWidth="1px"
                borderRightColor="gray.200"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                width="280px"
                minWidth="280px"
                maxWidth="280px"
                boxShadow="2px 0 4px rgba(0, 0, 0, 0.05)"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                css={{
                  marginLeft: '1px',
                  '--webkit-transform': 'translateZ(0)',
                  transform: 'translateZ(0)',
                }}
              >
                NAME
              </TableColumnHeader>
              {/* Scrollable columns with sticky top for vertical scrolling */}
              <TableColumnHeader
                position="sticky"
                top={0}
                zIndex={12}
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                minWidth="200px"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                CONNECTED CHATBOTS
              </TableColumnHeader>
              <TableColumnHeader
                position="sticky"
                top={0}
                zIndex={12}
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                minWidth="120px"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                LABEL
              </TableColumnHeader>
              <TableColumnHeader
                position="sticky"
                top={0}
                zIndex={12}
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                minWidth="120px"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                STATUS
              </TableColumnHeader>
              <TableColumnHeader
                position="sticky"
                top={0}
                zIndex={12}
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                minWidth="100px"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                TYPE
              </TableColumnHeader>
              <TableColumnHeader
                position="sticky"
                top={0}
                zIndex={12}
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                minWidth="180px"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                REQUIRES AUTH
              </TableColumnHeader>
              <TableColumnHeader
                position="sticky"
                top={0}
                zIndex={12}
                bg="white"
                borderBottomWidth="1px"
                borderBottomColor="gray.200"
                minWidth="120px"
                py={3}
                px={3}
                fontSize="xs"
                fontWeight="semibold"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                ACTIONS
              </TableColumnHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {channels?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} textAlign="center" py={6}>
                  No channels found
                </TableCell>
              </TableRow>
            ) : (
              channels?.map((channel) => (
                <TableRow
                  key={channel.id}
                  transition="background-color 0.2s"
                  _hover={{
                    backgroundColor: 'gray.50',
                  }}
                >
                  {/* Fixed NAME Cell */}
                  <TableCell
                    position="sticky"
                    left="0"
                    zIndex={9}
                    bg="white"
                    borderRightWidth="1px"
                    borderRightColor="gray.200"
                    width="280px"
                    minWidth="280px"
                    maxWidth="280px"
                    py={3}
                    px={3}
                    css={{
                      marginLeft: '1px',
                      '--webkit-transform': 'translateZ(0)',
                      transform: 'translateZ(0)',
                    }}
                  >
                    <Flex align="center" gap={3}>
                      <Icon
                        as={getChannelIcon(channel.type)}
                        color={getChannelIconColor(channel.type)}
                        boxSize={5}
                      />
                      <Text fontWeight="semibold" color="gray.900" truncate>
                        {channel.name}
                      </Text>
                    </Flex>
                  </TableCell>
                  {/* Scrollable cells */}
                  <TableCell minWidth="200px" py={3} px={3}>
                    {channelChatbots[channel.id] === undefined ? (
                      <Spinner size="xs" />
                    ) : channelChatbots[channel.id] ? (
                      <Link
                        onClick={() => navigate(`/chatbots/${channelChatbots[channel.id]?.id}/edit`)}
                        color="blue.600"
                        fontWeight="medium"
                        _hover={{ color: 'blue.700', textDecoration: 'underline', cursor: 'pointer' }}
                        fontSize="sm"
                      >
                        {channelChatbots[channel.id]?.name}
                      </Link>
                    ) : (
                      <Text color="gray.400" fontSize="sm">
                        No chatbot
                      </Text>
                    )}
                  </TableCell>
                  <TableCell minWidth="120px" py={3} px={3}>
                    <Text color="gray.700" fontSize="sm">
                      {channel.label || '-'}
                    </Text>
                  </TableCell>
                  <TableCell minWidth="120px" py={3} px={3}>
                    {renderStatus(channel)}
                  </TableCell>
                  <TableCell minWidth="100px" py={3} px={3}>
                    {renderType(channel.type)}
                  </TableCell>
                  <TableCell minWidth="180px" py={3} px={3}>
                    {renderRequiresAuth(channel.requires_authentication)}
                  </TableCell>
                  <TableCell minWidth="120px" py={3} px={3}>
                    <Tooltip
                      content={
                        channel.is_active
                          ? 'Deactivate Channel'
                          : 'Activate Channel'
                      }
                    >
                      <IconButton
                        aria-label={
                          channel.is_active ? 'Deactivate' : 'Activate'
                        }
                        size="sm"
                        variant="outline"
                        borderRadius="xl"
                        loading={transitioningChannels.includes(channel.id)}
                        onClick={() => handleToggleActive(channel)}
                        bg={channel.is_active ? 'red.50' : 'green.50'}
                        borderColor={
                          channel.is_active ? 'red.200' : 'green.200'
                        }
                        color={channel.is_active ? 'red.600' : 'green.600'}
                        _hover={{
                          bg: channel.is_active ? 'red.100' : 'green.100',
                          borderColor: channel.is_active
                            ? 'red.300'
                            : 'green.300',
                          color: channel.is_active ? 'red.700' : 'green.700',
                        }}
                        _active={{
                          bg: channel.is_active ? 'red.200' : 'green.200',
                          borderColor: channel.is_active
                            ? 'red.400'
                            : 'green.400',
                        }}
                        transition="all 0.2s"
                      >
                        <FiPower />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </TableRoot>
      </Box>

      {/* Pagination Controls */}
      <Box
        mt={6}
        py={4}
        px={6}
        bg="gray.50"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Flex justify="space-between" align="center">
          <HStack gap={6}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              Total:{' '}
              <Text as="span" fontWeight="bold" color="gray.900">
                {total}
              </Text>{' '}
              channels
            </Text>

            <HStack gap={3}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Rows per page:
              </Text>
              <MenuRoot>
                <MenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    bg="white"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                  >
                    {perPage}
                    <FiChevronRight />
                  </Button>
                </MenuTrigger>
                <MenuContent>
                  {[10, 25, 50, 100].map((value) => (
                    <MenuItem
                      key={value}
                      value={value.toString()}
                      onClick={() => {
                        setPerPage(value);
                        setPage(1); // Reset to first page when changing per page
                      }}
                    >
                      {value}
                    </MenuItem>
                  ))}
                </MenuContent>
              </MenuRoot>
            </HStack>
          </HStack>

          <HStack gap={4}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {total > 0
                ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} of ${total}`
                : '0 items'}
            </Text>
            <HStack gap={1}>
              <IconButton
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                variant="outline"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { bg: 'white', borderColor: 'gray.300' },
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <IconButton
                aria-label="Next page"
                disabled={page * perPage >= total}
                onClick={() => setPage(page + 1)}
                variant="outline"
                size="sm"
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
                _disabled={{
                  opacity: 0.4,
                  cursor: 'not-allowed',
                  _hover: { bg: 'white', borderColor: 'gray.300' },
                }}
              >
                <FiChevronRight />
              </IconButton>
            </HStack>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
}
